import {
  fitCaptureSize,
  SCREEN_SHARE_VIDEO_CONSTRAINTS,
} from '@/audio/displayCaptureConstraints'

export const SCREEN_CAPTURE_INTERVAL_MS = 5_000
export const SCREEN_CAPTURE_JPEG_QUALITY = 0.8

export type ScreenCaptureState = 'idle' | 'capturing' | 'paused' | 'error'

/** JSON image event on the shared STT WebSocket (text only — never binary). */
export interface ScreenFrameMessage {
  event: 'image'
  image: string
}

export interface ScreenCaptureStartOptions {
  meetingId: string
  /** Borrowed display stream (e.g. from tab-audio). Tracks are not stopped on stop() unless ownsStream. */
  stream?: MediaStream
  /** When true, stop() ends tracks on `stream`. Defaults to false when stream is provided, true when created internally. */
  ownsStream?: boolean
  /** Sends a JSON `{ event: "image", image }` payload on the shared STT socket. Returns false when not ready. */
  sendFrame: (message: ScreenFrameMessage) => boolean
  intervalMs?: number
  jpegQuality?: number
  onStateChange?: (state: ScreenCaptureState) => void
  onError?: (error: Error) => void
}

export interface ScreenCaptureSession {
  getState: () => ScreenCaptureState
  start: (options: ScreenCaptureStartOptions) => Promise<void>
  pause: () => void
  resume: () => void
  stop: () => void
}

export function buildImageEvent(image: string): ScreenFrameMessage {
  return {
    event: 'image',
    image,
  }
}

export async function blobToBase64(blob: Blob): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error ?? new Error('Failed to encode screen frame'))
    reader.readAsDataURL(blob)
  })
  const comma = dataUrl.indexOf(',')
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl
}

function createHiddenVideo(): HTMLVideoElement {
  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  video.autoplay = true
  // Must not use display:none — browsers may stop decoding and yield black frames.
  Object.assign(video.style, {
    position: 'fixed',
    width: '1px',
    height: '1px',
    opacity: '0.01',
    pointerEvents: 'none',
    left: '-9999px',
    top: '0',
    zIndex: '-1',
  })
  document.body.appendChild(video)
  return video
}

/**
 * Creates a screen/tab capture session that encodes JPEG frames and sends them
 * as JSON `{ event: "image", image }` events through the shared STT WebSocket.
 */
export function createScreenCaptureSession(): ScreenCaptureSession {
  let state: ScreenCaptureState = 'idle'
  let intervalMs = SCREEN_CAPTURE_INTERVAL_MS
  let jpegQuality = SCREEN_CAPTURE_JPEG_QUALITY
  let sendFrame: ((message: ScreenFrameMessage) => boolean) | null = null
  let onStateChange: ((next: ScreenCaptureState) => void) | undefined
  let onError: ((error: Error) => void) | undefined

  let stream: MediaStream | null = null
  let ownsStream = false
  let video: HTMLVideoElement | null = null
  let canvas: HTMLCanvasElement | null = null
  let captureIntervalId: ReturnType<typeof setInterval> | null = null
  let capturingFrame = false
  let videoEndedHandler: (() => void) | null = null
  let startGeneration = 0

  function getState(): ScreenCaptureState {
    return state
  }

  function setState(next: ScreenCaptureState): void {
    state = next
    onStateChange?.(next)
  }

  function setVideoTracksEnabled(enabled: boolean): void {
    stream?.getVideoTracks().forEach((track) => {
      track.enabled = enabled
    })
  }

  function stopFrameLoop(): void {
    if (captureIntervalId !== null) {
      clearInterval(captureIntervalId)
      captureIntervalId = null
    }
  }

  function cleanupResources(): void {
    stopFrameLoop()

    const track = stream?.getVideoTracks()[0]
    if (track && videoEndedHandler) {
      track.removeEventListener('ended', videoEndedHandler)
    }
    videoEndedHandler = null

    if (video) {
      video.srcObject = null
      video.remove()
      video = null
    }
    canvas = null

    if (stream) {
      if (ownsStream) {
        stream.getTracks().forEach((track) => track.stop())
      } else {
        setVideoTracksEnabled(false)
      }
      stream = null
    }
    ownsStream = false
    sendFrame = null
  }

  async function publishFrame(blob: Blob): Promise<void> {
    if (!sendFrame) return
    try {
      const image = await blobToBase64(blob)
      if (state !== 'capturing') return
      sendFrame(buildImageEvent(image))
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error(String(error)))
    }
  }

  async function captureFrame(): Promise<void> {
    if (state !== 'capturing' || capturingFrame) return
    if (!video || !canvas) return
    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return
    if (!video.videoWidth || !video.videoHeight) return

    capturingFrame = true
    try {
      // Encode a downscaled copy, not the native frame: a Retina full-screen
      // share is ~7.7MP, and JPEG-encoding that on the main thread every 5s is
      // felt as a periodic stutter (BRAI-343).
      const { width, height } = fitCaptureSize(video.videoWidth, video.videoHeight)
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.drawImage(video, 0, 0, width, height)

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas!.toBlob((result) => resolve(result), 'image/jpeg', jpegQuality)
      })
      if (!blob) return
      if (state !== 'capturing') return

      await publishFrame(blob)
    } finally {
      capturingFrame = false
    }
  }

  function startFrameLoop(): void {
    stopFrameLoop()
    void captureFrame()
    captureIntervalId = setInterval(() => {
      void captureFrame()
    }, intervalMs)
  }

  function attachVideoEndedListener(): void {
    const track = stream?.getVideoTracks()[0]
    if (!track) return
    videoEndedHandler = () => {
      onError?.(new Error('Screen sharing stopped by the user.'))
      stop()
    }
    track.addEventListener('ended', videoEndedHandler)
  }

  async function attachVideoElement(mediaStream: MediaStream): Promise<void> {
    const nextVideo = createHiddenVideo()
    video = nextVideo
    canvas = document.createElement('canvas')
    nextVideo.srcObject = mediaStream
    await nextVideo.play().catch(() => {
      /* autoplay may reject when muted policies differ; frames still arrive once ready */
    })
    if (nextVideo.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || !nextVideo.videoWidth) {
      await new Promise<void>((resolve) => {
        const done = () => {
          nextVideo.removeEventListener('loadeddata', done)
          resolve()
        }
        nextVideo.addEventListener('loadeddata', done)
        window.setTimeout(done, 1_000)
      })
    }
  }

  async function start(options: ScreenCaptureStartOptions): Promise<void> {
    if (state === 'capturing' || state === 'paused') {
      return
    }

    const generation = ++startGeneration
    intervalMs = options.intervalMs ?? SCREEN_CAPTURE_INTERVAL_MS
    jpegQuality = options.jpegQuality ?? SCREEN_CAPTURE_JPEG_QUALITY
    sendFrame = options.sendFrame
    onStateChange = options.onStateChange
    onError = options.onError

    try {
      if (options.stream && options.stream.getVideoTracks().some((t) => t.readyState === 'live')) {
        stream = options.stream
        ownsStream = options.ownsStream ?? false
      } else {
        const nextStream = await navigator.mediaDevices.getDisplayMedia({
          video: SCREEN_SHARE_VIDEO_CONSTRAINTS,
          audio: false,
        })
        if (generation !== startGeneration) {
          nextStream.getTracks().forEach((track) => track.stop())
          return
        }
        stream = nextStream
        ownsStream = true
      }

      if (generation !== startGeneration) return

      setVideoTracksEnabled(true)
      attachVideoEndedListener()
      await attachVideoElement(stream)
      if (generation !== startGeneration) return

      setState('capturing')
      startFrameLoop()
    } catch (error) {
      if (generation !== startGeneration) return
      cleanupResources()
      setState('error')
      const err = error instanceof Error ? error : new Error(String(error))
      onError?.(err)
      throw err
    }
  }

  function pause(): void {
    if (state !== 'capturing') return
    stopFrameLoop()
    setVideoTracksEnabled(false)
    // Stop the hidden element decoding too. Disabling the track only blanks the
    // frames — the element keeps decoding and compositing them for the whole
    // pause otherwise.
    video?.pause()
    setState('paused')
  }

  function resume(): void {
    if (state !== 'paused') return
    setVideoTracksEnabled(true)
    void video?.play().catch(() => {
      /* a live MediaStream resumes at the current frame; a rejected play just
         means the next tick reads a stale frame, not a broken session */
    })
    startFrameLoop()
    setState('capturing')
  }

  function stop(): void {
    startGeneration += 1
    cleanupResources()
    setState('idle')
  }

  return {
    getState,
    start,
    pause,
    resume,
    stop,
  }
}
